import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { AuthenticatedUser, PermissionCode } from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { removeSessionPrivateQueries } from '../../lib/session-query-cache';

import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context';
import { authApi } from './auth.api';
import { clearAccessToken, getAccessToken, setAccessToken } from './token-store';

/**
 * Owns the session for the whole application.
 *
 * On mount it asks the API to exchange the refresh cookie for an access token.
 * That is the only way a reload can restore a session, because the access
 * token is held in memory and is gone the moment the tab reloads.
 */
export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);

  // Read inside callbacks that must not be re-created when the user changes.
  const userRef = useRef<AuthenticatedUser | null>(null);
  const sessionGeneration = useRef(0);

  const replaceIdentity = useCallback(
    (identity: AuthenticatedUser) => {
      if (userRef.current?.id !== identity.id) removeSessionPrivateQueries(queryClient);
      userRef.current = identity;
      setUser(identity);
    },
    [queryClient],
  );

  const applySession = useCallback(
    (token: string, identity: AuthenticatedUser) => {
      replaceIdentity(identity);
      setAccessToken(token);
      setStatus('authenticated');
    },
    [replaceIdentity],
  );

  const clearSession = useCallback(() => {
    removeSessionPrivateQueries(queryClient);
    clearAccessToken();
    userRef.current = null;
    setUser(null);
    setStatus('anonymous');
  }, [queryClient]);

  /**
   * Exchanges the refresh cookie for a new access token.
   *
   * Wired into the API client so any request that meets a 401 can recover
   * without every caller knowing about tokens. Returns null when the session
   * is genuinely over, which tells the client to surface the original error.
   */
  const refresh = useCallback(async (): Promise<string | null> => {
    const generation = sessionGeneration.current;
    try {
      const session = await authApi.refresh();
      if (generation !== sessionGeneration.current) return null;
      applySession(session.accessToken, session.user);

      return session.accessToken;
    } catch {
      if (generation === sessionGeneration.current) clearSession();

      return null;
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    apiClient.configureAuth({ getAuthToken: getAccessToken, refreshAuthToken: refresh });
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    const generation = sessionGeneration.current;

    const bootstrap = async (): Promise<void> => {
      apiClient.configureAuth({ getAuthToken: getAccessToken, refreshAuthToken: refresh });

      try {
        const session = await authApi.refresh();

        if (cancelled || generation !== sessionGeneration.current) return;

        // Store the access token before `/auth/me` so bootstrap does not trigger
        // a second refresh rotation through the 401 retry path.
        setAccessToken(session.accessToken);

        const identity = await authApi.me();

        if (cancelled || generation !== sessionGeneration.current) return;

        applySession(session.accessToken, identity);
      } catch {
        // No cookie, or an expired one. An anonymous visitor is the normal
        // case here, not an error worth reporting.
        if (!cancelled && generation === sessionGeneration.current) {
          clearSession();
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession, refresh]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const generation = ++sessionGeneration.current;
      const session = await authApi.login(email, password);
      if (generation !== sessionGeneration.current) return;
      applySession(session.accessToken, session.user);
    },
    [applySession],
  );

  const signOut = useCallback(async (): Promise<void> => {
    ++sessionGeneration.current;
    // Remove private data immediately, even if logout fails or finishes after a new login.
    clearSession();
    await authApi.logout();
  }, [clearSession]);

  const reload = useCallback(async (): Promise<void> => {
    const generation = sessionGeneration.current;
    const identity = await authApi.me();
    if (generation === sessionGeneration.current) replaceIdentity(identity);
  }, [replaceIdentity]);

  const can = useCallback(
    (permission: PermissionCode): boolean =>
      userRef.current?.permissions.includes(permission) ?? false,
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut, reload, can }),
    [status, user, signIn, signOut, reload, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
